/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Omc } from '@src/app/model/Omc';
import { AbstractObject } from '@src/app/model/abstract-object';
import { MyInjector } from '@src/app/app.module';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FieldSetDialogComponent, FieldSetDialogOptions } from '@src/app/shared/dialogs/field-set-dialog/field-set-dialog.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { XeroService } from '@src/app/modules/importer/import-page/xero.service';
import { Field } from '@src/app/shared/field/Field';
import { required } from '@src/app/shared/validators';
import { AbstractControl, ValidationErrors } from '@angular/forms';

export interface TemplateParams {
    companyName: string;
    legalName: string;
    yearEndDay: number;
    yearEndMonth: number;
    numberOfUnits: number;
    partialAddress: string;
    firstLineOfAddress: string;
    generateUnitData: 'none' | 'basic' | 'sample';
    generateSampleContactData: 'none' | 'owners' | 'sample';
    generateSampleBudgetCodes: boolean;
    generateSampleSuppliers: boolean;
    generateSampleServiceChargeHistory: boolean;
    generateSamplePurchaseHistory: boolean;
}

export class DownloadTemplateAction implements IFormAction {
    name = $localize`Download Template`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'download';
    approvalNeeded = false;
    approvalText = '';
    disabled = false;
    tipText = $localize`Download an Excel template pre-filled with your OMC details`;

    private months = [
        { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
        { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
        { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
        { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' }
    ];

    private days = Array.from({ length: 31 }, (_, i) => ({ id: i + 1, name: ('0' + (i + 1)).slice(-2) }));

    setup(_omc: Omc) {
        this.show = true;
    }

    action(omc: Omc) {
        const dialog = MyInjector.instance.get(MatDialog);
        const xeroService = MyInjector.instance.get(XeroService);

        const companyNameField = FormTextComponent.make('Company Name', 'companyName', {
            hint: 'Short name for the company',
            validators: [required],
        });
        const legalNameField = FormTextComponent.make('Legal Name', 'legalName', {
            hint: 'Legal name as it appears on statements',
            validators: [required],
        });
        const yearEndMonthField = FormPicklistComponent.make('Year End Month', 'yearEndMonth', null, {
            items: this.months,
        }).override({ validators: [required] });
        const yearEndDayField = FormPicklistComponent.make('Year End Day', 'yearEndDay', null, {
            items: this.days,
        }).override({ validators: [required] });
        const numberOfUnitsField = FormNumberComponent.make('Number of Units', 'numberOfUnits', null, {
            hint: 'Total number of units in the development',
        }).override({ validators: [required] });
        const partialAddressField = FormTextAreaComponent.make('Partial Address', 'partialAddress', {
            hint: 'Town, city, county etc. — appended below the first line on all unit addresses'
        });

        let unitDataMode = 'basic';

        const firstLineOfAddressField = FormTextComponent.make('First Line of Address', 'firstLineOfAddress', {
            formColumn: 2,
            hint: 'Street name used to build each unit\'s address, e.g. "Castle Way". Unit number is prefixed automatically.',
            validators: [(_control: AbstractControl): ValidationErrors | null => {
                if (unitDataMode === 'none') return null;
                return _control.value?.trim() ? null : { required: true };
            }],
        });
        const generateContactDataField = FormPicklistComponent.make('Contact Data', 'generateSampleContactData', null, {
            items: [
                {id: 'none',   name: 'No Contact Data'},
                {id: 'owners', name: 'Create Owner Placeholder'},
                {id: 'sample', name: 'Full Samples'},
            ],
        }).override({
            formColumn: 2,
            hint: 'Owner Placeholder creates one Owner Occupier row per unit. Full Samples adds realistic contacts for the first 10 units.',
        });
        const generateUnitDataField = FormPicklistComponent.make('Generate Unit Data', 'generateUnitData', null, {
            items: [
                {id: 'none',   name: 'No Unit Data'},
                {id: 'basic',  name: 'Name & Address Only'},
                {id: 'sample', name: 'Full Samples'},
            ],
            refreshes: [(selected: AbstractObject) => {
                unitDataMode = (selected as {id: string})?.id ?? 'none';
                const active = unitDataMode !== 'none';
                firstLineOfAddressField.visible = active ? Field.formOnly : Field.noForm;
                generateContactDataField.visible = active ? Field.formOnly : Field.noForm;
            }],
        }).override({
            formColumn: 2,
            hint: 'Name & Address Only populates unit names and addresses. Full Samples adds type, size, bedrooms, floor and car park data for the first 10 units.',
        });
        const generateBudgetCodesField = FormCheckboxComponent.make('Generate Sample Budget Codes', 'generateSampleBudgetCodes', {
            formColumn: 2,
            hint: 'Populate the budget codes sheet with a typical OMC chart of accounts.'
        });
        const generateSuppliersField = FormCheckboxComponent.make('Generate Sample Suppliers', 'generateSampleSuppliers', {
            formColumn: 2,
            hint: 'Add one sample supplier per expense category.'
        });
        const generateARField = FormCheckboxComponent.make('Generate Sample Service Charge History', 'generateSampleServiceChargeHistory', {
            formColumn: 2,
            hint: 'Generate previous year service charges for all units, with 80% showing a receipt (paid) and 2 units with a prior balance forward.'
        });
        const generateAPField = FormCheckboxComponent.make('Generate Sample Purchase History', 'generateSamplePurchaseHistory', {
            formColumn: 2,
            hint: 'Generate purchase invoices and payments for the previous year across all expense categories, totalling close to service charge income.'
        });

        const fieldSet = new FieldSet({
            fields: [
                companyNameField,
                legalNameField,
                yearEndMonthField,
                yearEndDayField,
                numberOfUnitsField,
                partialAddressField,
                firstLineOfAddressField,
                generateUnitDataField,
                generateContactDataField,
                generateBudgetCodesField,
                generateSuppliersField,
                generateARField,
                generateAPField,
            ],
            formLayout: LAYOUT_OPTIONS.doubleCol,
        }, true); // initFormGroup = true so we can patch values and wire valueChanges immediately

        fieldSet.setValue({
            companyName: omc?.name ?? '',
            legalName: omc?.legalName ?? '',
            yearEndMonth: omc?.yearEndMonth ?? null,
            yearEndDay: omc?.yearEndDay ?? null,
            numberOfUnits: omc?.expectedUnits ?? null,
            partialAddress: omc?.address ?? '',
            generateUnitData: 'basic',
            generateSampleContactData: 'owners',
        } as unknown as AbstractObject);

        const dialogOptions: FieldSetDialogOptions = {
            fieldSet,
            title: $localize`Download Template`,
            minHeight: '480px',
            minWidth: '720px',
            saveText: $localize`Download`,
            saveFn: (values: TemplateParams) => {
                xeroService.getTemplate(values);
                return of({} as AbstractObject);
            }
        };

        dialog.open(FieldSetDialogComponent, { data: dialogOptions });
        return of(omc);
    }
}
